// Hash-based routing. The SPA is served from a single CloudFront origin with
// 403/404 rewritten to index.html, so the path after `#/` is the only client
// route. Query string after the path is preserved (login ?returnTo=).

export type Route =
  | { kind: "login"; returnTo: string }
  | { kind: "dashboard" }
  | { kind: "projects" }
  | { kind: "projectDetail"; projectId: string }
  | { kind: "book"; projectId: string }
  | { kind: "itemDetail"; projectId: string; itemId: string }
  | { kind: "newBook" }
  | { kind: "review" }
  | { kind: "jobs"; status?: string; day?: string }
  | { kind: "routes" }
  | { kind: "templates" }
  | { kind: "tokens" }
  | { kind: "exports" };

export function parseHash(hash = location.hash): Route {
  const raw = hash.replace(/^#\/?/, "");
  const q = raw.indexOf("?");
  const path = q === -1 ? raw : raw.slice(0, q);
  const search = new URLSearchParams(q === -1 ? "" : raw.slice(q + 1));
  const segments = path.split("/").filter(Boolean);

  if (segments[0] === "login") {
    const returnTo = search.get("returnTo") || "/";
    return { kind: "login", returnTo: returnTo.startsWith("/") ? returnTo : `/${returnTo}` };
  }
  if (segments[0] === "new-book") return { kind: "newBook" };
  // The book view lives under the project path; match it before the generic
  // `#/projects/:id` arm or it would silently render the project detail page.
  if (segments[0] === "projects" && segments[1] && segments[2] === "book") {
    return { kind: "book", projectId: segments[1] };
  }
  if (segments[0] === "projects" && segments[1]) {
    return { kind: "projectDetail", projectId: segments[1] };
  }
  if (segments[0] === "projects") {
    return { kind: "projects" };
  }
  if (segments[0] === "items" && segments[1] && segments[2]) {
    return { kind: "itemDetail", projectId: segments[1], itemId: segments[2] };
  }
  if (segments[0] === "review") return { kind: "review" };
  if (segments[0] === "jobs") {
    const status = search.get("status") || undefined;
    const day = search.get("day") || undefined;
    return { kind: "jobs", ...(status ? { status } : {}), ...(day ? { day } : {}) };
  }
  if (segments[0] === "routes") return { kind: "routes" };
  if (segments[0] === "templates") return { kind: "templates" };
  if (segments[0] === "tokens") return { kind: "tokens" };
  if (segments[0] === "exports") return { kind: "exports" };
  return { kind: "dashboard" };
}

export function hashForLogin(returnTo: string): string {
  const path = returnTo.startsWith("/") ? returnTo : `/${returnTo}`;
  const safe = path.startsWith("/login") ? "/" : path;
  return `#/login?returnTo=${encodeURIComponent(safe)}`;
}

export function pathForRoute(route: Route): string {
  switch (route.kind) {
    case "login":
      return hashForLogin(route.returnTo).slice(1);
    case "dashboard":
      return "/";
    case "projects":
      return "/projects";
    case "projectDetail":
      return `/projects/${route.projectId}`;
    case "book":
      return `/projects/${route.projectId}/book`;
    case "itemDetail":
      return `/items/${route.projectId}/${route.itemId}`;
    case "newBook":
      return "/new-book";
    case "review":
      return "/review";
    case "jobs": {
      const params = new URLSearchParams();
      if (route.status) params.set("status", route.status);
      if (route.day) params.set("day", route.day);
      const qs = params.toString();
      return qs ? `/jobs?${qs}` : "/jobs";
    }
    case "routes":
      return "/routes";
    case "templates":
      return "/templates";
    case "tokens":
      return "/tokens";
    case "exports":
      return "/exports";
  }
}

export function hashForRoute(route: Route): string {
  return `#${pathForRoute(route)}`;
}
